import { useState } from 'react';

import Checkbox from '../../../../components/Checkbox';
import { Drawer } from '../../../../components/Drawer';
import { HelpDrawerLink } from '../../../../components/HelpDrawerLink';
import { WorkflowSubStepProps } from '../../config';

export interface CertificationFormData {
  certificationAccepted: boolean;
}

function CertificationPage({ data, fieldErrors, updateData }: WorkflowSubStepProps<CertificationFormData>) {
  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false);
  const baseTestId = 'certification-page';

  const handleCertificationChange = (checked: boolean) => {
    updateData({
      ...data,
      certificationAccepted: checked,
    });
  };

  return (
    <div data-testid={baseTestId}>
      <div
        className='ds-u-margin-bottom--4'
        data-testid={`${baseTestId}-content`}>
        <h3
          className='ds-u-margin-bottom--2 certification-heading'
          data-testid={`${baseTestId}-heading`}>
          Certification
        </h3>

        <div
          className='ds-u-padding-left--2 field-max-width'
          data-testid={`${baseTestId}-statement`}>
          <p className='ds-u-margin-bottom--2'>
            <strong>I certify</strong> that, to the best of my knowledge and belief, all of the information on and
            attached to this Declaration for Federal Employment, including any attached application materials, is true,
            correct, complete, and made in good faith.{' '}
            <strong>
              I understand that a false or fraudulent answer to any question or item on any part of this declaration or
              its attachments may be grounds for not hiring me, or for firing me after I begin work, and may be
              punishable by fine or imprisonment.
            </strong>
          </p>

          <p className='ds-u-margin-bottom--2'>
            <strong>I understand</strong> that any information I give may be investigated for purposes of determining
            eligibility for Federal employment as allowed by law or Presidential order.
          </p>

          <p className='ds-u-margin-bottom--3'>
            <strong>I consent</strong> to the release of information about my ability and fitness for Federal employment
            by employers, schools, law enforcement agencies, and other individuals and organizations to investigators,
            personnel specialists, and other authorized employees or representatives of the Federal Government. I
            understand that for financial or lending institutions, medical institutions, hospitals, health care
            professionals, and some other sources of information, a separate specific release may be needed, and I may
            be contacted for such a release at a later date.
          </p>
        </div>
      </div>

      <div
        className='ds-u-margin-bottom--3 ds-u-padding-left--2 field-max-width'
        data-testid={`${baseTestId}-acceptance`}>
        <fieldset
          className='ds-c-fieldset'
          data-testid={`${baseTestId}-fieldset`}>
          <legend className='ds-c-label'>
            Do you certify, understand, and consent to the above?
            <span
              aria-hidden='true'
              className='ds-u-color--error'>
              *
            </span>
            <span className='sr-only'>(required)</span>
          </legend>

          <div
            className='ds-u-margin-top--2'
            data-testid={`${baseTestId}-checkbox`}>
            <Checkbox
              checked={data.certificationAccepted === true}
              data-testid={`${baseTestId}-checkbox-input`}
              errorMessage={fieldErrors?.certificationAccepted}
              id='certification-accepted'
              label='I certify, understand, and consent to the above.'
              name='certificationAccepted'
              value='true'
              onChange={(e) => handleCertificationChange(e.target.checked)}
            />
          </div>
        </fieldset>
      </div>

      <div
        className='ds-u-border-top--2 ds-u-padding-top--3 ds-u-margin-top--4 field-max-width'
        data-testid={`${baseTestId}-help`}>
        <HelpDrawerLink
          data-testid={`${baseTestId}-help-link`}
          onClick={() => setIsHelpDrawerOpen(true)}
        />
      </div>

      <Drawer
        data-testid={`${baseTestId}-drawer`}
        heading='Help: Certification'
        isOpen={isHelpDrawerOpen}
        onClose={() => setIsHelpDrawerOpen(false)}>
        <div className='ds-u-padding--3'>
          <h3 className='ds-u-margin-top--0'>Get Help with Certification</h3>
          <p>
            Check the box to confirm that the answers you added to this application are correct and complete. Please
            note that giving false or fraudulent data may result in:
          </p>

          <ul>
            <li>Not being hired for the position</li>
            <li>Firing you from your job, even if you are already employed.</li>
            <li>Charging you with a crime that may result in fines or imprisonment.</li>
          </ul>

          <h4 className='ds-u-margin-top--3'>Information Release</h4>
          <p>
            By checking the box, you agree that the Federal Government may verify the details in your application. They
            might contact your employers, schools, or law enforcement agencies to do this.
          </p>
        </div>
      </Drawer>
    </div>
  );
}

export default CertificationPage;
